import React, { useEffect, useRef, useState } from 'react';
import { OpenSheetMusicDisplay, TransposeCalculator } from 'opensheetmusicdisplay';
import {
  drawRibbon, buildScoreClock, scoreTimeAt, ribbonXAt, scoreTimeAtX, playbackTimeAt, notesAt,
  versesToDraw, verseFor, onsetAt, createGlide
} from './magnifier';

/**
 * The magnifier strip (Display > Lyrics > Magnify): my part drawn large on
 * one long line (magnifier.js) that glides under a fixed line as it plays,
 * the note being sung lit like the score's own highlight.
 * The drawings are made ahead, one per verse playback reaches, while the
 * song is loaded and stopped; while playing only a transform changes. While
 * playing, only the frame loop moves the strip (createGlide smooths the
 * player's ~20-30 playhead reports a second); while paused, a report does.
 *
 * With `onSeek`, the strip can be dragged to rewind or fast-forward: it
 * follows the finger (the lit note and, through `onScrub`, the words with
 * it) while the music carries on, and letting go moves the playhead there.
 */

// Where the music being sung crosses the strip (fraction of its width)
const ANCHOR = 0.3;
// Show about this many bars at once where the strip is wide enough
const BARS_SHOWN = 2;
// Draw the part at least this many times the score's own size, height allowing
const MIN_MAGNIFY = 1.5;
// Gap between drawings, so the page stays responsive (ms)
const DRAW_GAP_MS = 400;
// A press has to move this far to become a drag (px)
const DRAG_START_PX = 6;
// The note being sung
const LIT_COLOR = '#e53e3e';
const NOTHING_LIT = [];

// Colour (or restore) the paths of drawn notes: noteheads fill, stems stroke
const paint = (groups, color) => groups.forEach((group) => {
  group.querySelectorAll('path').forEach((path) => {
    path.style.fill = color;
    path.style.stroke = color;
  });
});
const light = (ribbon, notes) => {
  if (ribbon.lit === notes) return;
  if (ribbon.lit) paint(ribbon.lit, '');
  paint(notes, LIT_COLOR);
  ribbon.lit = notes;
};

const LyricsMagnifier = ({ controls, partId, partVerses, verse, playing, onSeek = null, onScrub = null }) => {
  const viewRef = useRef(null);
  const [ribbons, setRibbons] = useState(() => new Map()); // verse -> drawing
  const controlsRef = useRef(controls);
  controlsRef.current = controls;
  const xml = controls.getCurrentFileContent?.() || null;
  const transpose = controls.transposeSteps || 0;
  const { isLoading, respectRepeats } = controls;
  const versesKey = (partVerses || []).join(',');

  // ----- drawings: one per verse playback reaches, one at a time -----
  useEffect(() => {
    setRibbons(new Map());
    if (!xml || isLoading || partId === null || partId === undefined) return undefined;
    let cancelled = false;
    let timer = null;
    const verses = versesKey ? versesKey.split(',') : [];
    const queue = versesToDraw(buildScoreClock(controlsRef.current.getNotesWithTiming?.(), partId), verses, verse);
    const drawNext = () => {
      if (cancelled || queue.length === 0) return;
      const which = queue.shift();
      drawRibbon({ OSMD: OpenSheetMusicDisplay, TransposeCalculator, xml, partId, transpose, verse: which })
        .then((ribbon) => { if (!cancelled) setRibbons(previous => new Map(previous).set(which, ribbon)); })
        .catch((error) => { if (!cancelled) console.warn('Magnifier: could not draw the part', error); })
        .finally(() => { if (!cancelled) timer = setTimeout(drawNext, DRAW_GAP_MS); });
    };
    timer = setTimeout(drawNext, DRAW_GAP_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [xml, isLoading, partId, transpose, versesKey, verse, respectRepeats]);

  // ----- placing a drawing at a playback time -----
  const sizeRef = useRef({ width: 0, height: 0 });
  const shownRef = useRef(null);
  const clockRef = useRef({ events: null, length: 0, partId: null, onsets: [] });
  const glideRef = useRef(null);
  if (!glideRef.current) glideRef.current = createGlide();
  const placedRef = useRef(0); // the playback time the strip shows
  const dragRef = useRef(null);
  const placeRef = useRef(() => {});
  placeRef.current = (t) => {
    placedRef.current = t;
    const { width, height } = sizeRef.current;
    if (!width || !height || ribbons.size === 0) return;
    const events = controlsRef.current.getNotesWithTiming?.() || null;
    const clock = clockRef.current;
    if (clock.events !== events || clock.length !== (events?.length || 0) || clock.partId !== partId) {
      clockRef.current = { events, length: events?.length || 0, partId, onsets: buildScoreClock(events, partId) };
    }
    const { onsets } = clockRef.current;
    const onset = onsetAt(onsets, t);
    const ribbon = ribbons.get(verseFor(onset, partVerses, verse)) || ribbons.values().next().value;
    if (shownRef.current !== ribbon) {
      ribbons.forEach((r) => {
        r.svg.style.visibility = r === ribbon ? 'visible' : 'hidden';
        if (r !== ribbon) light(r, NOTHING_LIT);
      });
      shownRef.current = ribbon;
    }
    light(ribbon, onset && t < onset.end ? notesAt(ribbon.points, onset.abs) : NOTHING_LIT);
    const { box, barWidth } = ribbon;
    const zoom = controlsRef.current.zoom || 1;
    const scale = Math.min(height / box.height, Math.max(width / (BARS_SHOWN * barWidth), MIN_MAGNIFY * zoom));
    if (ribbon.scale !== scale) {
      ribbon.scale = scale;
      ribbon.svg.setAttribute('viewBox', `${box.x} ${box.y} ${box.width} ${box.height}`);
      ribbon.svg.setAttribute('width', String(box.width * scale));
      ribbon.svg.setAttribute('height', String(box.height * scale));
    }
    const x = ribbonXAt(ribbon.points, scoreTimeAt(onsets, t));
    const dx = -(x - box.x) * scale + width * ANCHOR;
    const dy = (height - box.height * scale) / 2;
    ribbon.svg.style.transform = `translate3d(${dx.toFixed(1)}px, ${dy.toFixed(1)}px, 0)`;
  };

  // The drawings live in the strip, all but the current one hidden
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return undefined;
    ribbons.forEach((ribbon) => {
      ribbon.scale = null;
      ribbon.svg.style.visibility = 'hidden';
      view.appendChild(ribbon.svg);
    });
    shownRef.current = null;
    placeRef.current(glideRef.current.time ?? 0);
    return () => ribbons.forEach(ribbon => ribbon.svg.remove());
  }, [ribbons]);

  // Size of the strip (zero while the panel is hidden)
  useEffect(() => {
    const view = viewRef.current;
    if (!view || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => {
      sizeRef.current = { width: entry.contentRect.width, height: entry.contentRect.height };
      placeRef.current(glideRef.current.time ?? 0);
    });
    observer.observe(view);
    return () => observer.disconnect();
  }, []);

  // ----- following playback -----
  const playingRef = useRef(playing);
  playingRef.current = playing;
  const { addPlayheadListener } = controls;
  useEffect(() => {
    if (!addPlayheadListener) return undefined;
    return addPlayheadListener((t) => {
      glideRef.current.report(t, performance.now());
      if (t !== null && !playingRef.current && !dragRef.current?.moved) placeRef.current(t);
    });
  }, [addPlayheadListener]);

  // While playing, the frame loop alone moves the strip (the finger, while dragging)
  useEffect(() => {
    if (!playing) return undefined;
    const glide = glideRef.current;
    glide.reset();
    let frame = requestAnimationFrame(function step(now) {
      const t = glide.at(now);
      if (t !== null && !dragRef.current?.moved) placeRef.current(t);
      frame = requestAnimationFrame(step);
    });
    return () => cancelAnimationFrame(frame);
  }, [playing]);

  // ----- dragging to rewind or fast-forward -----
  const timeUnderDrag = (drag, clientX) => {
    const { points } = drag.ribbon;
    const x = Math.min(points[points.length - 1].x, Math.max(points[0].x, drag.x - (clientX - drag.clientX) / drag.scale));
    return playbackTimeAt(clockRef.current.onsets, scoreTimeAtX(points, x), drag.from);
  };
  const onPointerDown = (e) => {
    const ribbon = shownRef.current;
    if (!onSeek || !ribbon?.scale || e.button > 0) return;
    // A mouse press would otherwise start selecting the strip's words
    e.preventDefault();
    const from = placedRef.current;
    dragRef.current = {
      clientX: e.clientX,
      x: ribbonXAt(ribbon.points, scoreTimeAt(clockRef.current.onsets, from)),
      scale: ribbon.scale,
      ribbon,
      from,
      time: from,
      moved: false
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag || (!drag.moved && Math.abs(e.clientX - drag.clientX) < DRAG_START_PX)) return;
    if (!drag.moved) viewRef.current?.classList.add('dragging');
    drag.moved = true;
    drag.time = timeUnderDrag(drag, e.clientX);
    placeRef.current(drag.time);
    onScrub?.(drag.time);
  };
  const endDrag = (commit) => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    if (!drag.moved) return;
    viewRef.current?.classList.remove('dragging');
    onScrub?.(null);
    if (commit && onSeek) {
      glideRef.current.reset();
      glideRef.current.report(drag.time, performance.now());
      onSeek(drag.time);
    } else {
      placeRef.current(glideRef.current.time ?? drag.from);
    }
  };

  return (
    <div
      className={`lyrics-ribbon ${onSeek ? 'seekable' : ''}`}
      ref={viewRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={() => endDrag(true)}
      onPointerCancel={() => endDrag(false)}
    >
      <div className="lyrics-ribbon-line" />
    </div>
  );
};

export default LyricsMagnifier;
